import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import type { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { isValidHexColor } from '@openx/utils/lib/isValidHexColor.ts';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Cell, Pie, PieChart as RechartsPieChart, Tooltip } from 'recharts';
import { palette } from '../../theme/paletteV2.ts';
import { NoDataOrLoadingState } from './components/NoDataOrLoadingState.tsx';
import { TooltipContent } from './components/TooltipContent.tsx';
import { useLabelWithPosition } from './hooks/useLabelWithPosition.tsx';

export type PieChartDataItem = {
  name: string;
  value: number;
  color?: string;
  labelColor?: string;
};

export type PieChartProps = {
  title?: string;
  data: PieChartDataItem[];
  defaultColor?: string;
  defaultLabelColor?: string;
  containerSx?: SxProps;
  loading?: boolean;
  isAnimationActive?: boolean;
  hideLabels?: boolean;
  hideLegend?: boolean;
};

const DEFAULT_COLOR = palette.charts.purple;
const DEFAULT_LABEL_COLOR = palette.common.white;

type PieChartCoreProps = {
  data: PieChartDataItem[];
  defaultColor?: string;
  defaultLabelColor?: string;
  isLoading?: boolean;
  isAnimationActive?: boolean;
  hideLabels?: boolean;
  showTooltip?: boolean;
  tooltipContent?: ReactNode;
  width?: number;
  height?: number;
  outerRadius?: number;
  innerRadius?: number;
};

export const PieChartCore = ({
  data,
  defaultColor,
  defaultLabelColor,
  isLoading = false,
  isAnimationActive = true,
  hideLabels = false,
  showTooltip = false,
  tooltipContent,
  width = 200,
  height = 200,
  outerRadius = 100,
  innerRadius = 50,
}: PieChartCoreProps) => {
  const colorDefault = isValidHexColor(defaultColor) ? defaultColor : DEFAULT_COLOR;
  const labelColorDefault =
    defaultLabelColor && isValidHexColor(defaultLabelColor) ? defaultLabelColor : DEFAULT_LABEL_COLOR;

  const shouldDisplayValue = useCallback((value: number) => {
    return typeof value === 'number' && value > 0;
  }, []);

  const dataWithColors = useMemo(() => {
    return data.map(item => ({
      ...item,
      color: isValidHexColor(item.color) ? item.color : colorDefault,
      labelColor: isValidHexColor(item.labelColor) ? item.labelColor : labelColorDefault,
    }));
  }, [data, colorDefault, labelColorDefault]);

  const filteredData = useMemo(() => {
    return dataWithColors.filter(item => shouldDisplayValue(item.value));
  }, [dataWithColors, shouldDisplayValue]);

  const totalValueSum = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + item.value, 0);
  }, [filteredData]);

  const areAllColorsDifferent = useMemo(() => {
    const colors = filteredData.map(item => item.color);
    return new Set(colors).size === colors.length;
  }, [filteredData]);

  const { labelWithPosition } = useLabelWithPosition({
    data: filteredData,
    labelColorDefault,
    totalValueSum,
  });

  if (totalValueSum === 0 || isLoading) {
    return null;
  }

  return (
    <RechartsPieChart height={height} width={width}>
      <Pie
        data={filteredData}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="50%"
        outerRadius={outerRadius}
        innerRadius={innerRadius}
        stroke={areAllColorsDifferent ? 'none' : undefined}
        label={hideLabels ? false : labelWithPosition}
        labelLine={false}
        isAnimationActive={isAnimationActive}
      >
        {filteredData.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.color} />
        ))}
      </Pie>
      {showTooltip && tooltipContent}
    </RechartsPieChart>
  );
};

export const PieChart = ({
  title,
  data,
  defaultColor,
  defaultLabelColor,
  containerSx,
  loading = false,
  isAnimationActive = true,
  hideLabels = false,
  hideLegend = false,
}: PieChartProps) => {
  const colorDefault = isValidHexColor(defaultColor) ? defaultColor : DEFAULT_COLOR;
  const labelColorDefault =
    defaultLabelColor && isValidHexColor(defaultLabelColor) ? defaultLabelColor : DEFAULT_LABEL_COLOR;

  const shouldDisplayValue = useCallback((value: number) => {
    return typeof value === 'number' && value > 0;
  }, []);

  const dataWithColors = useMemo(() => {
    return data.map(item => ({
      ...item,
      color: isValidHexColor(item.color) ? item.color : colorDefault,
      labelColor: isValidHexColor(item.labelColor) ? item.labelColor : labelColorDefault,
    }));
  }, [data, colorDefault, labelColorDefault]);

  const filteredData = useMemo(() => {
    return dataWithColors.filter(item => shouldDisplayValue(item.value));
  }, [dataWithColors, shouldDisplayValue]);

  const totalValueSum = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + item.value, 0);
  }, [filteredData]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',

        ...containerSx,
      }}
    >
      {title && (
        <Typography
          variant="h3"
          align="center"
          data-test="pie-chart-title"
          sx={{
            color: 'text.secondary',
            mb: 3,
          }}
        >
          {title}
        </Typography>
      )}

      {totalValueSum > 0 && !loading ? (
        <PieChartCore
          data={data}
          defaultColor={defaultColor}
          defaultLabelColor={defaultLabelColor}
          isLoading={loading}
          isAnimationActive={isAnimationActive}
          hideLabels={hideLabels}
          showTooltip={true}
          tooltipContent={<Tooltip content={props => <TooltipContent {...props} totalValueSum={totalValueSum} />} />}
        />
      ) : (
        <NoDataOrLoadingState loading={loading} isAnimationActive={isAnimationActive && !loading} />
      )}

      {!hideLegend && (totalValueSum > 0 || loading) && (
        <Box
          data-test="pie-chart-legend"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
            mt: 3,
            px: 2,
          }}
        >
          {loading ? (
            <Skeleton animation="wave" variant="rectangular" width={164} height={28} />
          ) : (
            filteredData.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                  }}
                />
                <Typography variant="body1" sx={theme => ({ color: theme.palette.text.primary })}>
                  {item.name}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};
